import { Box, Button, Card, Container, Flex, Heading, Text } from '@radix-ui/themes';
import React, { Component, ErrorInfo, ReactNode } from "react";
import { useTranslation } from 'react-i18next';
import { Logo } from './ui/Logo';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, errorInfo: ErrorInfo) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Log to external service if needed
    // Sentry.captureException(error, { extra: errorInfo });
  }

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback(error, errorInfo!);
      }

      // Default error UI
      return <ErrorFallback error={error} errorInfo={errorInfo} />;
    }

    return children;
  }
}

// Hook for handling async errors
export function useAsyncError() {
  const [, setError] = React.useState();
  return React.useCallback(
    (error: Error) => {
      setError(() => {
        throw error;
      });
    },
    [setError]
  );
}

// CSS preload error handler
// Error Fallback Component with translations
function ErrorFallback({ error, errorInfo }: { error: Error; errorInfo: ErrorInfo | null }) {
  // We can't use hooks in class components, so we'll use a wrapper
  return <ErrorFallbackInner error={error} errorInfo={errorInfo} />;
}

function ErrorFallbackInner({ error, errorInfo }: { error: Error; errorInfo: ErrorInfo | null }) {
  const { t } = useTranslation();

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      minHeight="100vh"
      p="4"
    >
      <Container size="3" style={{ maxWidth: '800px', textAlign: 'center' }}>
        <Box mb="8">
          <Logo align="center" />
        </Box>
        <Heading size="8" mb="4">
          {t('errors.something_went_wrong', 'Something went wrong')}
        </Heading>
        <Text size="4" color="gray" style={{ display: 'block', marginBottom: '32px' }}>
          {t('errors.unexpected_error_message', "We're sorry, but something unexpected happened.")}
        </Text>
        {error.message && (
          <Box mb="6">
            <Card size="2" variant="surface" style={{ backgroundColor: 'var(--red-2)', display: 'inline-block' }}>
              <Text size="2" color="red" style={{ fontFamily: 'monospace' }}>
                {error.message}
              </Text>
            </Card>
          </Box>
        )}
        <Button
          size="4"
          onClick={() => window.location.reload()}
        >
          {t('errors.reload_page', 'Reload Page')}
        </Button>
        {process.env.NODE_ENV === "development" && errorInfo && (
          <Box mt="6">
            <details style={{ textAlign: 'left', maxWidth: '600px', margin: '0 auto' }}>
              <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400">
                Error Details (Development Only)
              </summary>
              <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-auto">
                {errorInfo.componentStack}
              </pre>
            </details>
          </Box>
        )}
      </Container>
    </Flex>
  );
}

export function setupCSSPreloadErrorHandler() {
  if (typeof window !== "undefined") {
    // Handle CSS preload errors
    window.addEventListener("error", (event) => {
      if (
        event.target instanceof HTMLLinkElement &&
        event.target.rel === "preload" &&
        event.target.as === "style"
      ) {
        console.error("CSS preload failed:", event.target.href);
        // Optionally retry loading without preload
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = event.target.href;
        document.head.appendChild(link);
      }
    }, true);

    // Handle unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
      console.error("Unhandled promise rejection:", event.reason);
    });
  }
}
